import { FRAUD_RULES, readVoucherSheet, sheetApprovals, type FraudRule, type SheetResult, type SheetRow, type TherapySession } from '@selfon/domain/browser';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth/AuthContext';
import { Alert, Chip, Empty, errorText, Loading, MonthPicker, PageHeader, useLoad } from '../components/ui';
import { dateTime, thisMonthKst, won } from '../format';
import { readTable, SheetError } from '../sheet';

interface Row { approvalNo: string; clientId: string; approvedAt: string; amount: number; program: string; therapistId?: string }
type Finding = { approvalNo: string; clientId: string; rules: string[]; score: number; level: 'OK' | 'REVIEW' | 'HIGH' };

const toLocalInput = (iso: string) => new Date(new Date(iso).getTime() + 9 * 3600_000).toISOString().slice(0, 16);
const fromLocalInput = (v: string) => `${v}:00+09:00`;

/** 예시 승인내역: 완료 회기마다 정상 승인 1건 + 의심 사례 2건(수업 없는 일요일, 짧은 시간 중복 승인). */
function sampleRows(sessions: TherapySession[]): Row[] {
  // 예시는 10건만(달력 데모 데이터가 많아서)
  const done = sessions.filter((s) => s.status === 'COMPLETED' && s.voucherProgram).slice(0, 10);
  const rows: Row[] = done.map((s, i) => ({
    approvalNo: `A${String(i + 1).padStart(3, '0')}`, clientId: s.clientId, therapistId: s.therapistId,
    approvedAt: new Date(new Date(s.endAt).getTime() + 5 * 60_000).toISOString(), amount: 40000, program: s.voucherProgram!,
  }));
  const any = done[0];
  if (any) {
    const month = any.startAt.slice(0, 7);
    const sunday = [...Array(28).keys()].map((d) => `${month}-${String(d + 1).padStart(2, '0')}`).find((d) => new Date(`${d}T12:00:00+09:00`).getUTCDay() === 0);
    if (sunday) rows.push({ approvalNo: 'A901', clientId: any.clientId, therapistId: any.therapistId, approvedAt: `${sunday}T11:00:00+09:00`, amount: 55000, program: any.voucherProgram! });
    const last = rows[0]!;
    rows.push({ ...last, approvalNo: 'A902', approvedAt: new Date(new Date(last.approvedAt).getTime() + 3 * 60_000).toISOString() });
  }
  return rows;
}

/** 올릴 파일 양식 예시(가상 데이터). 포털 파일의 열 이름이 조금 달라도 비슷한 이름이면 알아본다. */
const TEMPLATE = '﻿승인번호,승인일자,승인시간,대상자명,생년월일,제공인력명,사업명,결제금액\r\nA0001,2026-10-05,14:30,가상아동,2018-05-05,가상선생,발달재활서비스,50000\r\n';

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv;charset=utf-8' }));
  Object.assign(document.createElement('a'), { href: url, download: '바우처_승인내역_양식.csv' }).click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Result({ f }: { f?: Finding }) {
  if (!f) return <span className="muted small">대조 전</span>;
  return (
    <div className="finding">
      <Chip tone={f.level === 'HIGH' ? 'danger' : f.level === 'REVIEW' ? 'warn' : 'ok'}>{f.level === 'HIGH' ? `위험 ${f.score}` : f.level === 'REVIEW' ? `확인 필요 ${f.score}` : '정상'}</Chip>
      {f.rules.map((rule) => <span key={rule} className="small">{FRAUD_RULES[rule as FraudRule]?.label ?? rule}</span>)}
    </div>
  );
}

const PICK = '이용자를 골라 주세요.';

/**
 * 바우처 대조: 전자바우처 승인내역과 회기(출결)를 맞춰 부정결제 위험을 점수로 보여준다.
 * 포털에서 내려받은 승인내역 파일(xlsx·csv)은 브라우저에서 읽어 이용자와 맞추고, 서버에는 이용자 id 로 바꾼 승인내역만 보낸다.
 * 파일이 없을 때는 이달 완료 회기로 만든 예시 행으로 시험한다.
 */
export function Voucher() {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const [month, setMonth] = useState(thisMonthKst());
  const sessions = useLoad(() => api.sessions(centerId, month), [centerId, month]);
  const clients = useLoad(() => api.clients(centerId), [centerId]);
  const staff = useLoad(() => api.staff(centerId), [centerId]);
  const [rows, setRows] = useState<Row[]>([]);
  const [sheet, setSheet] = useState<{ fileName: string; result: SheetResult } | null>(null);
  const [chosen, setChosen] = useState<Map<number, string>>(new Map());
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const clientList = clients.data?.clients ?? [];
  const name = new Map(clientList.map((c) => [c.id, c.displayName]));
  const ready = !!clients.data && !!staff.data;

  useEffect(() => { if (sessions.data) { setRows(sampleRows(sessions.data.sessions)); setFindings(null); } }, [sessions.data]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setReading(true); setFileError(null); setFindings(null);
    try {
      const result = readVoucherSheet(await readTable(file), {
        clients: clientList.map((c) => ({ id: c.id, displayName: c.displayName, birthYear: c.birthYear })),
        staff: (staff.data?.staff ?? []).map((s) => ({ userId: s.userId, name: s.name })),
      });
      if (result.error) setFileError(result.error);
      else if (!result.rows.length) setFileError('파일에 승인내역 행이 없습니다.');
      else {
        setSheet({ fileName: file.name, result });
        setChosen(new Map());
        if (result.months[0]) setMonth(result.months[0]);
      }
    } catch (e) {
      setFileError(e instanceof SheetError ? e.message : '파일을 읽지 못했습니다. xlsx 또는 csv 파일인지 확인하세요.');
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const approvals = sheet ? sheetApprovals(sheet.result.rows, month, chosen) : rows;
  const run = async () => {
    setBusy(true); setError(null);
    try { setFindings((await api.voucherAudit(centerId, { month, approvals, source: sheet ? 'file' : 'sample' })).findings); }
    catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const byNo = new Map((findings ?? []).map((f) => [f.approvalNo, f]));
  const flagged = (findings ?? []).filter((f) => f.level !== 'OK').length;

  /** 파일 행이 대조에서 빠지는 이유(없으면 대조 대상). */
  const skipReason = (r: SheetRow) =>
    r.problem ?? (!r.approvedAt?.startsWith(month) ? `${r.approvedAt?.slice(0, 7)} 승인이라 이 달 대조에서 뺍니다.` : !(r.clientId ?? chosen.get(r.line)) ? PICK : undefined);
  const pick = (line: number, id: string) => {
    const m = new Map(chosen);
    if (id) m.set(line, id); else m.delete(line);
    setChosen(m); setFindings(null);
  };

  return (
    <>
      <PageHeader title="바우처 대조"
        actions={<MonthPicker value={month} onChange={(m) => { setMonth(m); setFindings(null); }} />} />

      <section className="panel" aria-labelledby="vu-title">
        <h2 id="vu-title">승인내역 파일</h2>
        <p className="muted small">전자바우처 포털 승인내역(xlsx·csv). 파일은 서버로 보내지 않습니다.</p>
        <div className="row-actions">
          <input id="vu-file" ref={fileRef} className="sr-only" type="file" accept=".xlsx,.xls,.csv,.txt"
            disabled={reading || !ready} onChange={(e) => void onFile(e.target.files?.[0])} />
          <button type="button" className="btn btn-primary" disabled={reading || !ready} onClick={() => fileRef.current?.click()}>{reading ? '읽는 중…' : '파일 올리기'}</button>
          <button type="button" className="btn" onClick={downloadTemplate}>양식 내려받기</button>
          {sheet && <button type="button" className="btn" onClick={() => { setSheet(null); setFindings(null); }}>예시로 돌아가기</button>}
        </div>
        {fileError && <Alert>{fileError}</Alert>}
        {sheet && <p className="small" role="status">{sheet.fileName}: {sheet.result.rows.length}행 중 {approvals.length}건을 {month} 대조에 씁니다.</p>}
      </section>

      {!sheet && rows.length > 0 && <p className="muted small">예시 승인내역</p>}
      {(sessions.error || clients.error || staff.error) && <Alert>{sessions.error ?? clients.error ?? staff.error}</Alert>}
      {sessions.loading && !sessions.data && <Loading />}
      {!sheet && sessions.data && rows.length === 0 && <Empty>{month}에 바우처 사업으로 완료된 회기가 없어 예시를 만들 수 없습니다.</Empty>}

      {sheet && (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{sheet.fileName} 승인내역과 대조 결과</caption>
            <thead><tr><th scope="col">행</th><th scope="col">승인번호</th><th scope="col">파일의 대상자</th><th scope="col">센터 이용자</th><th scope="col">승인 일시</th><th scope="col" className="num">금액</th><th scope="col">대조 결과</th></tr></thead>
            <tbody>
              {sheet.result.rows.map((r) => {
                const skip = skipReason(r);
                const options = r.candidates.length > 1 ? r.candidates : clientList.map((c) => c.id);
                return (
                  <tr key={r.line} className={skip && skip !== PICK ? 'row-skipped' : undefined}>
                    <td className="mono">{r.line}</td>
                    <td className="mono">{r.approvalNo || '-'}</td>
                    <td>{r.name || '-'}</td>
                    <td>
                      {r.clientId ? name.get(r.clientId) : r.problem ? '-' : (
                        <>
                          <label className="sr-only" htmlFor={`vc-${r.line}`}>{r.line}행 센터 이용자</label>
                          <select id={`vc-${r.line}`} value={chosen.get(r.line) ?? ''} onChange={(e) => pick(r.line, e.target.value)}>
                            <option value="">{r.candidates.length > 1 ? '같은 이름이 여럿 - 고르기' : '센터에 없는 이름 - 고르기'}</option>
                            {options.map((id) => <option key={id} value={id}>{name.get(id)} ({clientList.find((c) => c.id === id)?.birthYear}년생)</option>)}
                          </select>
                        </>
                      )}
                    </td>
                    <td>{r.approvedAt ? dateTime(r.approvedAt) : '-'}</td>
                    <td className="num">{r.amount === undefined ? '-' : won(r.amount)}</td>
                    <td>{skip ? <span className="small muted">{skip}</span> : <Result f={byNo.get(r.approvalNo)} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!sheet && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{month} 바우처 승인내역과 대조 결과</caption>
            <thead><tr><th scope="col">승인번호</th><th scope="col">이용자</th><th scope="col">승인 일시</th><th scope="col" className="num">금액</th><th scope="col">대조 결과</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.approvalNo}>
                  <td className="mono">{r.approvalNo}</td>
                  <td>{name.get(r.clientId) ?? r.clientId}</td>
                  <td>
                    <label className="sr-only" htmlFor={`va-${i}`}>{r.approvalNo} 승인 일시</label>
                    <input id={`va-${i}`} type="datetime-local" value={toLocalInput(r.approvedAt)}
                      onChange={(e) => { setRows(rows.map((x, j) => (j === i ? { ...x, approvedAt: fromLocalInput(e.target.value) } : x))); setFindings(null); }} />
                  </td>
                  <td className="num">{won(r.amount)}</td>
                  <td><Result f={byNo.get(r.approvalNo)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(sheet || rows.length > 0) && (
        <>
          {error && <Alert>{error}</Alert>}
          <div className="row">
            <span className="muted small">{findings ? `${approvals.length}건 중 ${flagged}건이 의심 사례입니다. ${dateTime(new Date().toISOString())} 대조` : '대조를 실행하면 규칙별 위험 점수가 표시됩니다.'}</span>
            <button type="button" className="btn btn-primary" onClick={run} disabled={busy || approvals.length === 0}>{busy ? '대조 중…' : '대조 실행'}</button>
          </div>
        </>
      )}
    </>
  );
}
